import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Toolbar, Divider } from '@mui/material';

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function Body({ children }) {
  return (
    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1 }}>
      {children}
    </Typography>
  );
}

function BulletList({ items }) {
  return (
    <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 1 }}>
      {items.map(item => (
        <Box component="li" key={item} sx={{ mb: 0.5 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function Privacy() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Helmet>
        <title>Privacy Policy — Turf</title>
        <meta name="description" content="Read Turf's privacy policy to understand how we handle your data when you use our football scores and fixtures app." />
        <link rel="canonical" href="https://turf-app.io/privacy" />
        <meta property="og:url" content="https://turf-app.io/privacy" />
        <meta property="og:title" content="Privacy Policy — Turf" />
        <meta property="og:description" content="Read Turf's privacy policy to understand how we handle your data when you use our football scores and fixtures app." />
      </Helmet>
      <Toolbar />
      <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 3, md: 4 }, py: { xs: 6, md: 8 } }}>

        <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1, mb: 1 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', mb: 5 }}>
          Last updated: January 20, 2026
        </Typography>

        <Divider sx={{ mb: 5 }} />

        <Body>
          Turf ("we", "our", or "the app") respects your privacy. This Privacy Policy explains how
          information is handled when you use the Turf iOS app.
        </Body>

        <Box sx={{ mt: 4 }}>
          <Section title="Information We Collect">
            <Body>Turf is designed to collect as little personal data as possible.</Body>
            <Body>We collect:</Body>
            <BulletList items={[
              'App usage data (e.g. feature interactions, screen views) to improve app performance and user experience.',
              'Device information such as iOS version and device model for debugging and compatibility.',
              'Push notification tokens, if you enable notifications, to deliver match alerts and updates.',
            ]} />
            <Body>We do not collect:</Body>
            <BulletList items={[
              'Names, email addresses, or contact details',
              'Precise location data',
              'Payment information (handled entirely by Apple via the App Store)',
            ]} />
          </Section>

          <Section title="How We Use Information">
            <Body>Any data collected is used solely to:</Body>
            <BulletList items={[
              'Provide a personalised and up to date experience',
              'Improve app reliability and performance',
              'Diagnose crashes and technical issues',
            ]} />
          </Section>

          <Section title="Data Storage & Security">
            <BulletList items={[
              'Data is stored securely using industry-standard safeguards.',
              'Data is retained only for as long as necessary to operate and improve the app.',
            ]} />
          </Section>

          <Section title="Third-Party Services">
            <Body>
              Turf may use third-party services (such as analytics or crash reporting) to help improve the app.
            </Body>
          </Section>

          <Section title="Push Notifications">
            <Body>
              You can enable or disable notifications at any time in your device settings.
            </Body>
          </Section>

          <Section title="GDPR Rights">
            <Body>If you are located in the European Economic Area, you have the right to:</Body>
            <BulletList items={[
              'Access the personal data we hold about you',
              'Request correction of inaccurate data',
              'Request erasure of your data',
              'Restrict or object to our processing of your data',
              'Request portability of your data',
              'Object to processing based on legitimate interests',
            ]} />
            <Body>
              We process data based on legitimate interests (to operate and improve the app) and
              consent, where required.
            </Body>
          </Section>

          <Section title="CCPA Rights">
            <Body>If you are a California resident, you have the right to:</Body>
            <BulletList items={[
              'Know what personal information is collected about you',
              'Request deletion of your personal information',
              'Opt out of the sale of your personal information',
            ]} />
            <Body>
              Turf does not sell personal information and does not share personal information for
              cross-context advertising.
            </Body>
          </Section>

          <Section title="Children's Privacy">
            <Body>Turf is not directed at children under the age of 13.</Body>
          </Section>

          <Section title="Contact">
            <Body>
              If you have questions about this Privacy Policy, contact us at{' '}
              <Box
                component="a"
                href="mailto:support@turf-app.io"
                sx={{ color: '#22c55e', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                support@turf-app.io
              </Box>.
            </Body>
          </Section>
        </Box>
      </Box>
    </Box>
  );
}
